import React, { useEffect, useMemo, useState } from "react";
import { useSelector } from "react-redux";
import { RootState } from "../../store/store";
import {
  createEvent,
  getEventById,
  getEvents,
  IEventItem,
} from "../../services/eventsApi";
import { fetchEmployees } from "../../services/employeeApi";
import { getOwnerTaxes, getUserTaxes, TaxOption } from "../../services/taxApi";

type EmployeeOption = {
  _id: string;
  email: string;
  name?: string;
  linkedUserId: string;
};

type DailyTimeSlot = { startTime: string; endTime: string };

type EventDraft = {
  title: string;
  description: string;
  startDate: string;
  endDate: string;
  startTime: string;
  endTime: string;
  location: string;
};

const emptyDraft = (): EventDraft => ({
  title: "",
  description: "",
  startDate: "",
  endDate: "",
  startTime: "",
  endTime: "",
  location: "",
});

const parseDate = (value: string) => {
  const [year, month, day] = value.split("-").map(Number);
  return year && month && day ? new Date(year, month - 1, day) : null;
};

const dateKeysInRange = (startValue: string, endValue: string) => {
  const start = parseDate(startValue);
  const end = parseDate(endValue);
  if (!start || !end || start > end) return [];
  const dates: string[] = [];
  const cursor = new Date(start);
  while (cursor <= end) {
    const year = cursor.getFullYear();
    const month = String(cursor.getMonth() + 1).padStart(2, "0");
    const day = String(cursor.getDate()).padStart(2, "0");
    dates.push(`${year}-${month}-${day}`);
    cursor.setDate(cursor.getDate() + 1);
  }
  return dates;
};

const slotHours = (start?: string, end?: string) => {
  if (!start || !end) return 0;
  const [startHour, startMinute] = start.split(":").map(Number);
  const [endHour, endMinute] = end.split(":").map(Number);
  const minutes = endHour * 60 + endMinute - (startHour * 60 + startMinute);
  return minutes > 0 ? minutes / 60 : 0;
};

const combineDateTime = (date: string, time: string) =>
  new Date(`${date}T${time}:00`).toISOString();

const toDateInputValue = (value?: string) => {
  if (!value) return "";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "";
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, "0");
  const day = String(date.getDate()).padStart(2, "0");
  return `${year}-${month}-${day}`;
};

const formatOriginalEventDate = (value?: string) => {
  if (!value) return "";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "";
  return date.toLocaleDateString(undefined, {
    month: "short",
    day: "numeric",
    year: "numeric",
  });
};
const toUpcomingDateInputValue = (value?: string, targetYear?: number) => {
  if (!value) return "";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "";

  const today = new Date();
  const todayKey = `${today.getFullYear()}-${String(today.getMonth() + 1).padStart(2, "0")}-${String(today.getDate()).padStart(2, "0")}`;
  const monthIndex = date.getMonth();
  const dayOfMonth = date.getDate();

  const formatForYear = (year: number) => {
    const maxDay = new Date(year, monthIndex + 1, 0).getDate();
    const normalizedDay = Math.min(dayOfMonth, maxDay);
    const month = String(monthIndex + 1).padStart(2, "0");
    const day = String(normalizedDay).padStart(2, "0");
    return `${year}-${month}-${day}`;
  };

  if (typeof targetYear === "number") {
    return formatForYear(targetYear);
  }

  const currentYearValue = formatForYear(today.getFullYear());
  return currentYearValue >= todayKey
    ? currentYearValue
    : formatForYear(today.getFullYear() + 1);
};

const toTimeInputValue = (value?: string) => {
  if (!value) return "";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "";
  const hours = String(date.getHours()).padStart(2, "0");
  const minutes = String(date.getMinutes()).padStart(2, "0");
  return `${hours}:${minutes}`;
};

const dateLabel = (value: string) =>
  parseDate(value)?.toLocaleDateString(undefined, {
    weekday: "short",
    month: "short",
    day: "numeric",
  }) || value;

const errorMessage = (error: any, fallback: string) =>
  error?.response?.data?.message || error?.message || fallback;

const futureDateLimit = (() => {
  const date = new Date();
  date.setFullYear(date.getFullYear() + 10);
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, "0");
  const day = String(date.getDate()).padStart(2, "0");
  return `${year}-${month}-${day}`;
})();

type Props = {
  open: boolean;
  onClose: () => void;
  onCreated: () => Promise<void> | void;
};

export default function CreateEventModal({ open, onClose, onCreated }: Props) {
  const user = useSelector((state: RootState) => state.auth.user) as any;
  const currentUserId = String(user?._id || "");
  const canUseEmployees = user?.tier === "pro" || user?.tier === "elite";
  const [draft, setDraft] = useState<EventDraft>(emptyDraft);
  const [dailySlots, setDailySlots] = useState<Record<string, DailyTimeSlot>>(
    {},
  );
  const [employees, setEmployees] = useState<EmployeeOption[]>([]);
  const [previousEvents, setPreviousEvents] = useState<IEventItem[]>([]);
  const [taxes, setTaxes] = useState<TaxOption[]>([]);
  const [selectedEmployeeId, setSelectedEmployeeId] = useState(currentUserId);
  const [selectedTaxId, setSelectedTaxId] = useState("");
  const [loadingOptions, setLoadingOptions] = useState(false);
  const [saving, setSaving] = useState(false);
  const [applyingTemplate, setApplyingTemplate] = useState(false);
  const [formError, setFormError] = useState("");
  const [submitAttempted, setSubmitAttempted] = useState(false);

  const dates = useMemo(
    () => dateKeysInRange(draft.startDate, draft.endDate),
    [draft.startDate, draft.endDate],
  );
  const isMultiDay = dates.length > 1;
  const titleSuggestions = useMemo(() => {
    const query = draft.title.trim().toLowerCase();
    if (!query) return [];
    return previousEvents
      .filter((event) => {
        const title = String(event.title || "").trim().toLowerCase();
        return title.includes(query) && title !== query;
      })
      .slice(0, 6);
  }, [draft.title, previousEvents]);

  useEffect(() => {
    if (!open) return;
    setDraft(emptyDraft());
    setDailySlots({});
    setSelectedEmployeeId(currentUserId);
    setSelectedTaxId("");
    setFormError("");
    setSubmitAttempted(false);
    let active = true;
    (async () => {
      setLoadingOptions(true);
      const [employeeResult, taxResult, eventsResult] = await Promise.allSettled([
        canUseEmployees ? fetchEmployees() : Promise.resolve({ data: [] }),
        getUserTaxes().catch(() => getOwnerTaxes()),
        getEvents(),
      ]);
      if (!active) return;
      if (employeeResult.status === "fulfilled") {
        const raw = employeeResult.value?.data;
        const items = Array.isArray(raw) ? raw : raw?.employees;
        setEmployees(Array.isArray(items) ? items : []);
      }
      if (taxResult.status === "fulfilled") {
        setTaxes(taxResult.value);
        setSelectedTaxId(
          taxResult.value.find((tax) => tax.isDefault)?._id || "",
        );
      }
      if (eventsResult.status === "fulfilled") {
        setPreviousEvents(
          Array.isArray(eventsResult.value) ? eventsResult.value : [],
        );
      } else {
        setPreviousEvents([]);
      }
      setLoadingOptions(false);
    })();
    return () => {
      active = false;
    };
  }, [canUseEmployees, currentUserId, open]);

  useEffect(() => {
    if (!isMultiDay) {
      setDailySlots({});
      return;
    }
    setDailySlots((current) => {
      const next: Record<string, DailyTimeSlot> = {};
      dates.forEach((date) => {
        next[date] = current[date] || {
          startTime: draft.startTime,
          endTime: draft.endTime,
        };
      });
      return next;
    });
  }, [dates.join("|"), draft.endTime, draft.startTime, isMultiDay]);
  if (!open) return null;

  const close = () => {
    if (!saving) onClose();
  };

  const updateDraft = <K extends keyof EventDraft>(
    key: K,
    value: EventDraft[K],
  ) => setDraft((current) => ({ ...current, [key]: value }));

  const updateSlot = (date: string, key: keyof DailyTimeSlot, value: string) =>
    setDailySlots((current) => ({
      ...current,
      [date]: { ...current[date], [key]: value },
    }));

  const applyTemplate = async (templateEventId: string) => {
    try {
      setApplyingTemplate(true);
      setFormError("");
      const event = await getEventById(templateEventId);
      const startDate = toUpcomingDateInputValue(event.startDateTime);
      const templateYear = Number(startDate.split("-")[0] || new Date().getFullYear());
      const endSourceDate = event.endDateTime || event.startDateTime;
      let endDate = toUpcomingDateInputValue(endSourceDate, templateYear);
      if (endDate && endDate < startDate) {
        endDate = toUpcomingDateInputValue(endSourceDate, templateYear + 1);
      }
      const startTime = toTimeInputValue(event.startDateTime);
      const endTime = toTimeInputValue(event.endDateTime || event.startDateTime);
      const scheduleDates = dateKeysInRange(startDate, endDate);
      const nextDailySlots: Record<string, DailyTimeSlot> = {};

      if (Array.isArray(event.dailySchedule) && event.dailySchedule.length > 0) {
        event.dailySchedule.forEach((slot) => {
          const slotDate =
            toUpcomingDateInputValue(slot.date, templateYear) ||
            toUpcomingDateInputValue(slot.startDateTime, templateYear);
          if (!slotDate) return;
          nextDailySlots[slotDate] = {
            startTime:
              slot.startTime || toTimeInputValue(slot.startDateTime) || startTime,
            endTime:
              slot.endTime || toTimeInputValue(slot.endDateTime) || endTime,
          };
        });
      }

      scheduleDates.forEach((date) => {
        if (!nextDailySlots[date]) {
          nextDailySlots[date] = { startTime, endTime };
        }
      });

      setDraft({
        title: event.title || "",
        description: event.description || "",
        startDate,
        endDate,
        startTime,
        endTime,
        location: event.location || "",
      });
      setDailySlots(nextDailySlots);
      setSelectedEmployeeId(event.assignedEmployees?.[0] || currentUserId);
      setSelectedTaxId(event.selectedTaxes?.[0] || "");
    } catch (error) {
      setFormError(errorMessage(error, "Could not apply event template."));
    } finally {
      setApplyingTemplate(false);
    }
  };

  const applyFirstScheduleToAll = () => {
    const source = dates
      .map((date) => dailySlots[date])
      .find((slot) => slotHours(slot?.startTime, slot?.endTime) > 0);
    if (!source) return;
    setDailySlots(
      Object.fromEntries(dates.map((date) => [date, { ...source }])) as Record<
        string,
        DailyTimeSlot
      >,
    );
  };

  const handleSubmit = async (event: React.FormEvent) => {
    event.preventDefault();
    setSubmitAttempted(true);
    if (!draft.title.trim()) return setFormError("Please enter a title.");
    if (!draft.startDate || !draft.endDate)
      return setFormError("Please select an event date range.");
    if (!draft.location.trim()) return setFormError("Please enter a location.");

    if (!isMultiDay) {
      if (!draft.startTime || !draft.endTime)
        return setFormError("Please select start and end time.");
      if (slotHours(draft.startTime, draft.endTime) <= 0)
        return setFormError("End time must be after start time.");
    } else {
      for (const date of dates) {
        const slot = dailySlots[date];
        if (!slot?.startTime || !slot?.endTime)
          return setFormError(
            `Please set start and end time for ${dateLabel(date)}.`,
          );
        if (slotHours(slot.startTime, slot.endTime) <= 0)
          return setFormError(`Invalid time range on ${dateLabel(date)}.`);
      }
    }

    const schedule = isMultiDay
      ? dates.map((date) => {
          const slot = dailySlots[date];
          return {
            date,
            startTime: slot.startTime,
            endTime: slot.endTime,
            startDateTime: combineDateTime(date, slot.startTime),
            endDateTime: combineDateTime(date, slot.endTime),
            hours: slotHours(slot.startTime, slot.endTime),
          };
        })
      : undefined;
    const firstDate = isMultiDay ? dates[0] : draft.startDate;
    const lastDate = isMultiDay ? dates[dates.length - 1] : draft.endDate;
    const firstTime = isMultiDay
      ? dailySlots[firstDate].startTime
      : draft.startTime;
    const lastTime = isMultiDay ? dailySlots[lastDate].endTime : draft.endTime;

    try {
      setSaving(true);
      setFormError("");
      setSubmitAttempted(false);
      await createEvent({
        title: draft.title.trim(),
        description: draft.description.trim() || undefined,
        startDateTime: combineDateTime(firstDate, firstTime),
        endDateTime: combineDateTime(lastDate, lastTime),
        location: draft.location.trim(),
        status: "PUBLISHED",
        assignedEmployees: selectedEmployeeId ? [selectedEmployeeId] : [],
        selectedTaxes: selectedTaxId ? [selectedTaxId] : [],
        dailySchedule: schedule,
      });
      await onCreated();
      onClose();
    } catch (error) {
      setFormError(errorMessage(error, "Could not create event."));
    } finally {
      setSaving(false);
    }
  };

  const totalHours = dates.reduce((total, date) => {
    const slot = dailySlots[date];
    return total + slotHours(slot?.startTime, slot?.endTime);
  }, 0);
  const showTitleError = submitAttempted && !draft.title.trim();
  const showStartDateError = submitAttempted && !draft.startDate;
  const showEndDateError = submitAttempted && !draft.endDate;
  const showStartTimeError = submitAttempted && !isMultiDay && !draft.startTime;
  const showEndTimeError = submitAttempted && !isMultiDay && !draft.endTime;
  const showLocationError = submitAttempted && !draft.location.trim();
  const inputClass = (hasError: boolean) =>
    `mt-1.5 w-full rounded-lg border px-3 py-2.5 outline-none focus:ring-2 ${
      hasError
        ? "border-red-500 focus:border-red-500 focus:ring-red-100"
        : "border-slate-300 focus:border-blue-500 focus:ring-blue-100"
    }`;

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-slate-950/55 p-4"
      onMouseDown={(event) => event.target === event.currentTarget && close()}
    >
      <div
        role="dialog"
        aria-modal="true"
        aria-labelledby="create-event-title"
        className="max-h-[92vh] w-full max-w-3xl overflow-y-auto rounded-2xl bg-white shadow-2xl"
      >
        <div className="sticky top-0 z-10 flex items-center justify-between border-b border-slate-200 bg-white px-6 py-4">
          <div>
            <h2
              id="create-event-title"
              className="text-xl font-semibold text-slate-900"
            >
              Create event
            </h2>
            <p className="text-sm text-slate-500">
              Add the core event information.
            </p>
          </div>
          <button
            type="button"
            onClick={close}
            className="rounded-md px-2 py-1 text-2xl text-slate-400 hover:bg-slate-100"
            aria-label="Close"
          >
            X
          </button>
        </div>

        <form onSubmit={handleSubmit} className="space-y-5 p-6">
          <label className="block text-sm font-medium text-slate-700">
            Event title
            <div className="relative mt-1.5">
              <input
                autoFocus
                value={draft.title}
                onChange={(event) =>
                  updateDraft("title", event.target.value.trimStart())
                }
                maxLength={120}
                placeholder="Title"
                aria-invalid={showTitleError}
                className={`w-full rounded-lg border px-3 py-2.5 outline-none focus:ring-2 ${
                  showTitleError
                    ? "border-red-500 focus:border-red-500 focus:ring-red-100"
                    : "border-slate-300 focus:border-blue-500 focus:ring-blue-100"
                }`}
              />
              {titleSuggestions.length > 0 && (
                <div className="absolute left-0 right-0 top-full z-20 mt-2 overflow-hidden rounded-xl border border-slate-200 bg-white shadow-xl">
                  <div className="border-b border-slate-100 px-3 py-2 text-xs font-semibold uppercase tracking-wide text-slate-500">
                    Use a previous event
                  </div>
                  <div className="max-h-64 overflow-y-auto py-1">
                    {titleSuggestions.map((event) => (
                      <button
                        key={event._id}
                        type="button"
                        onClick={() => void applyTemplate(event._id)}
                        className="block w-full px-3 py-2.5 text-left hover:bg-slate-50"
                      >
                        <div className="text-sm font-semibold text-slate-900">
                          {event.title}
                        </div>
                        <div className="mt-0.5 text-xs text-slate-500">
                          {[
                            event.location,
                            formatOriginalEventDate(event.startDateTime),
                          ]
                            .filter(Boolean)
                            .join(" • ")}
                        </div>
                      </button>
                    ))}
                  </div>
                </div>
              )}
            </div>
            <span className="mt-1 block text-xs font-normal text-slate-400">
              Type a past event name to reuse its dates, times, location, and
              setup.
            </span>
          </label>

          <fieldset>
            <legend className="text-sm font-medium text-slate-700">
              Event date range
            </legend>
            <div className="mt-1.5 grid gap-4 sm:grid-cols-2">
              <label className="text-xs font-medium uppercase tracking-wide text-slate-500">
                Start date
                <input
                  type="date"
                  value={draft.startDate}
                  max={futureDateLimit}
                  onChange={(event) =>
                    updateDraft("startDate", event.target.value)
                  }
                  aria-invalid={showStartDateError}
                  className={`${inputClass(showStartDateError)} text-sm text-slate-800`}
                />
              </label>
              <label className="text-xs font-medium uppercase tracking-wide text-slate-500">
                End date
                <input
                  type="date"
                  value={draft.endDate}
                  min={draft.startDate || undefined}
                  max={futureDateLimit}
                  onChange={(event) =>
                    updateDraft("endDate", event.target.value)
                  }
                  aria-invalid={showEndDateError}
                  className={`${inputClass(showEndDateError)} text-sm text-slate-800`}
                />
              </label>
            </div>
          </fieldset>

          {!isMultiDay && (
            <fieldset>
              <legend className="text-sm font-medium text-slate-700">
                Event time range
              </legend>
              <div className="mt-1.5 grid gap-4 sm:grid-cols-2">
                <label className="text-xs font-medium uppercase tracking-wide text-slate-500">
                  Start time
                  <input
                    type="time"
                    value={draft.startTime}
                    onChange={(event) =>
                      updateDraft("startTime", event.target.value)
                    }
                    aria-invalid={showStartTimeError}
                    className={`${inputClass(showStartTimeError)} text-sm text-slate-800`}
                  />
                </label>
                <label className="text-xs font-medium uppercase tracking-wide text-slate-500">
                  End time
                  <input
                    type="time"
                    value={draft.endTime}
                    onChange={(event) =>
                      updateDraft("endTime", event.target.value)
                    }
                    aria-invalid={showEndTimeError}
                    className={`${inputClass(showEndTimeError)} text-sm text-slate-800`}
                  />
                </label>
              </div>
            </fieldset>
          )}

          {isMultiDay && (
            <section className="rounded-xl border border-blue-200 bg-blue-50/50 p-4">
              <div className="flex flex-col gap-2 sm:flex-row sm:items-start sm:justify-between">
                <div>
                  <h3 className="text-sm font-semibold text-slate-900">
                    Set time for each day
                  </h3>
                  <p className="text-xs text-slate-500">
                    Multi-day events use a separate start and end time for every
                    day.
                  </p>
                </div>
                <button
                  type="button"
                  onClick={applyFirstScheduleToAll}
                  disabled={
                    !dates.some(
                      (date) =>
                        slotHours(
                          dailySlots[date]?.startTime,
                          dailySlots[date]?.endTime,
                        ) > 0,
                    )
                  }
                  className="rounded-lg border border-blue-300 bg-white px-3 py-1.5 text-xs font-semibold text-blue-700 hover:bg-blue-50 disabled:cursor-not-allowed disabled:opacity-50"
                >
                  Apply first valid times to all days
                </button>
              </div>
              <div className="mt-4 max-h-72 space-y-3 overflow-y-auto pr-1">
                {dates.map((date) => {
                  const slot = dailySlots[date] || {
                    startTime: "",
                    endTime: "",
                  };
                  const hours = slotHours(slot.startTime, slot.endTime);
                  return (
                    <div
                      key={date}
                      className="grid items-end gap-3 rounded-lg border border-slate-200 bg-white p-3 sm:grid-cols-[1.2fr_1fr_1fr_auto]"
                    >
                      <div className="text-sm font-semibold text-slate-800">
                        {dateLabel(date)}
                      </div>
                      <label className="text-xs font-medium text-slate-500">
                        Start time
                        <input
                          type="time"
                          value={slot.startTime}
                          onChange={(event) =>
                            updateSlot(date, "startTime", event.target.value)
                          }
                          className="mt-1 w-full rounded-md border border-slate-300 px-2 py-1.5 text-sm text-slate-800"
                        />
                      </label>
                      <label className="text-xs font-medium text-slate-500">
                        End time
                        <input
                          type="time"
                          value={slot.endTime}
                          onChange={(event) =>
                            updateSlot(date, "endTime", event.target.value)
                          }
                          className="mt-1 w-full rounded-md border border-slate-300 px-2 py-1.5 text-sm text-slate-800"
                        />
                      </label>
                      <span
                        className={`pb-2 text-xs font-semibold ${hours > 0 ? "text-emerald-600" : "text-slate-400"}`}
                      >
                        {hours > 0 ? `${hours.toFixed(2)} hrs` : "Set times"}
                      </span>
                    </div>
                  );
                })}
              </div>
              <p className="mt-3 text-right text-xs font-semibold text-slate-600">
                Total scheduled hours: {totalHours.toFixed(2)}
              </p>
            </section>
          )}

          <label className="block text-sm font-medium text-slate-700">
            Location
            <input
              value={draft.location}
              onChange={(event) =>
                updateDraft("location", event.target.value.trimStart())
              }
              placeholder="Location: Address, City, or Zip"
              aria-invalid={showLocationError}
              className={inputClass(showLocationError)}
            />
          </label>

          <div className="grid gap-4 sm:grid-cols-2">
            <label className="block text-sm font-medium text-slate-700">
              Assigned to
              <select
                value={selectedEmployeeId}
                onChange={(event) => setSelectedEmployeeId(event.target.value)}
                disabled={!canUseEmployees || loadingOptions || applyingTemplate}
                className="mt-1.5 w-full rounded-lg border border-slate-300 bg-white px-3 py-2.5 outline-none focus:border-blue-500 focus:ring-2 focus:ring-blue-100 disabled:bg-slate-100"
              >
                <option value={currentUserId}>Myself</option>
                {employees.map((employee) => (
                  <option key={employee._id} value={employee.linkedUserId}>
                    {employee.name || employee.email}
                  </option>
                ))}
              </select>
              {!canUseEmployees && (
                <span className="mt-1 block text-xs font-normal text-slate-400">
                  Employee assignment is available on Pro and Elite.
                </span>
              )}
            </label>
            <label className="block text-sm font-medium text-slate-700">
              Tax rate
              <select
                value={selectedTaxId}
                onChange={(event) => setSelectedTaxId(event.target.value)}
                disabled={loadingOptions || applyingTemplate}
                className="mt-1.5 w-full rounded-lg border border-slate-300 bg-white px-3 py-2.5 outline-none focus:border-blue-500 focus:ring-2 focus:ring-blue-100 disabled:bg-slate-100"
              >
                <option value="">No Tax</option>
                {taxes.map((tax) => (
                  <option key={tax._id} value={tax._id}>
                    {tax.name} ({tax.rate}%)
                  </option>
                ))}
              </select>
            </label>
          </div>

          <label className="block text-sm font-medium text-slate-700">
            Description{" "}
            <span className="font-normal text-slate-400">(optional)</span>
            <textarea
              value={draft.description}
              onChange={(event) =>
                updateDraft("description", event.target.value)
              }
              rows={4}
              placeholder="Notes about this event"
              className="mt-1.5 w-full resize-y rounded-lg border border-slate-300 px-3 py-2.5 outline-none focus:border-blue-500 focus:ring-2 focus:ring-blue-100"
            />
          </label>

          {formError && (
            <div className="rounded-lg border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-700">
              {formError}
            </div>
          )}
          <div className="sticky bottom-0 flex justify-end gap-3 border-t border-slate-200 bg-white pt-5">
            <button
              type="button"
              onClick={close}
              disabled={saving}
              className="rounded-lg border border-slate-300 px-4 py-2 text-sm font-semibold text-slate-700 hover:bg-slate-50 disabled:opacity-50"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={saving || loadingOptions || applyingTemplate}
              className="rounded-lg bg-blue-600 px-4 py-2 text-sm font-semibold text-white hover:bg-blue-700 disabled:opacity-60"
            >
              {applyingTemplate
                ? "Applying template..."
                : saving
                  ? "Creating..."
                  : "Create event"}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}








